import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { observable, IObservableArray } from 'mobx';
import {
    IEntrySlideRenderCostKey, ISlideRenderStateManager, ISlideRenderStateServices,
    IStatePointCollection, IStatePointEnum, SLIDE_RENDER_ENTRY,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { initStatePoints } from '@src/slide/application/service/slide-render-state/state-point';
import { mountPlugins } from '@src/slide/application/service/slide-render-state/plugin';

/**
 * 渲染状态服务
 * 提供一系列渲染完成时机的通知
 */
export default class SlideRenderStateManager implements ISlideRenderStateManager {
    // 其他service注入完成的promise
    private servicesResolve: (value: ISlideRenderStateServices) => void;
    private servicesPromise: Promise<ISlideRenderStateServices> = new Promise(resolve =>
        this.servicesResolve = resolve);

    // 状态变更时机的集合
    private readonly statePoints: IStatePointCollection;
    // 不同入口的slide 渲染耗时记录map
    private entrySlideRenderCostMap: Map<IEntrySlideRenderCostKey, number> = new Map();
    // 当前文档的页数
    private pageSize: number;
    // 文档缩略图列表构建时包含的占位缩略图个数
    private listPanelSize = 0;
    // 文档缩略图首次渲染的时间
    private listCellFirstLoadTimeArr: number[] = [];
    // 可见slide的key（指的是缩略图）
    @observable
    private visibleSlideKeyList: IObservableArray<string> = observable.array([]);

    public constructor() {
        // 初始化状态变更时机集合
        this.statePoints = initStatePoints();
        // 监听slide convert完成
        this.observeSlideConverted();
        // 挂载强依赖渲染时机的几个插件
        mountPlugins(this);
    }

    public setServicesDelegate(params: ISlideRenderStateServices): void {
        this.servicesResolve(params);
    }

    public getServicesPromise(): Promise<ISlideRenderStateServices> {
        return this.servicesPromise;
    }

    /**
     * 记录文档页数
     * @param size
     */
    public setPageSize(size: number): void {
        this.pageSize = size;
        if (size === 0) {
            const renderCompleteTime = Math.round(performance.now());
            this.notifyCompleted(IStatePointEnum.visibleSlideRender, renderCompleteTime, SLIDE_RENDER_ENTRY.UNKNOWN);
            this.notifyCompleted(IStatePointEnum.firstSlideRender, renderCompleteTime, SLIDE_RENDER_ENTRY.UNKNOWN);
        }
    }

    /**
     * 标记当前文档的缩略图已构建，并记录缩略图个数
     */
    public setListPanelSize(size: number, entry: SLIDE_RENDER_ENTRY): void {
        this.listPanelSize = size;
        // 分析缩略图占位是否已完成
        this.analyseAllThumbnailSiteComplete(Math.round(performance.now()), entry);
    }

    /**
     * 添加露出的slide
     * @param slide
     */
    public addVisibleSlide(slide: ISlideModel): void {
        if (!this.visibleSlideKeyList.includes(slide.key)) {
            this.visibleSlideKeyList.push(slide.key);
        }
    }

    /**
     * 移除露出的slide
     * @param slide
     */
    public removeVisibleSlide(slideKey: string): void {
        const index = this.visibleSlideKeyList.indexOf(slideKey);
        if (index >= 0) {
            this.visibleSlideKeyList.splice(index, 1);
        }
    }

    /**
     * 记录各slide的渲染完成时间，并分析各时机是否到达
     * @param slideComponent
     * @param entry
     */
    public setSlideRenderCompleted(slideComponent: ISlideComponent, entry: SLIDE_RENDER_ENTRY): void {
        const { slideModel } = slideComponent.options;
        if (!slideModel?.childrenConverted || slideModel.preload || !slideComponent.hasBuild) {
            // 只有完成元素构建的渲染才有效
            return;
        }
        if (this.pageSize === undefined) {
            // 还没有页数信息，无需走后续分析流程了
            return;
        }

        const renderCompleteTime = Math.round(performance.now());
        const renderStateKey = this.getRenderSlideKey(entry, slideModel.key);
        this.entrySlideRenderCostMap.set(renderStateKey, renderCompleteTime);

        this.analyseFirstSlideRenderComplete(renderCompleteTime, entry);
        this.analyseVisibleSlideRenderComplete(renderCompleteTime, entry, slideModel);
        this.analyseEditSlideRenderComplete(renderCompleteTime, entry, slideModel);
    }

    /**
     * 获取各渲染时机
     */
    public getStatePoints(): IStatePointCollection {
        return this.statePoints;
    }

    /**
     * 获取文档页数
     */
    public getPageSize(): number | undefined {
        return this.pageSize;
    }

    /**
     * 获取缩略图渲染时间列表
     */
    public getListCellFirstLoadTimeArr(): number[] {
        return this.listCellFirstLoadTimeArr;
    }

    /**
     * 获取可见slide key集合
     */
    public getVisibleSlideKeyList(): IObservableArray<string> {
        return this.visibleSlideKeyList;
    }

    /**
     * 监听slide转换完成
     * 目前依赖于master和layout转换完成的时机，并不够准确
     * 这个时机后续待调整，先对齐以前的逻辑
     * @private
     */
    private observeSlideConverted() {
        eventBus.onIncludingPast(EventKeys.MASTER_LAYOUT_CONVERTED, async () => {
            // 确保所有的sdk-slide非空白占位slide
            await SlideDataServices.ContentStatusManager()?.waitForDocumentReady();
            const renderCompleteTime = Math.round(performance.now());
            this.notifyCompleted(IStatePointEnum.allSlideConvert, renderCompleteTime, SLIDE_RENDER_ENTRY.UNKNOWN);
        });
    }

    /**
     * 将入口和slideKey组合成唯一值
     * @param entry
     * @param slideKey
     * @private
     */
    private getRenderSlideKey(entry: SLIDE_RENDER_ENTRY, slideKey: string | undefined): IEntrySlideRenderCostKey {
        return `${slideKey || ''}-${entry}`;
    }

    /**
     * 分析可视区缩略图是否均渲染完成
     * @param renderCompleteTime
     * @param entry
     * @param slide
     * @private
     */
    private async analyseVisibleSlideRenderComplete(
        renderCompleteTime: number,
        entry: SLIDE_RENDER_ENTRY,
        slide: ISlideModel,
    ) {
        if ([SLIDE_RENDER_ENTRY.EDIT_PC, SLIDE_RENDER_ENTRY.EDIT_MOBILE].includes(entry)) {
            // 移动和PC的编辑区不参与
            return;
        }
        let visibleSlideRenderComplete = true;
        if ([SLIDE_RENDER_ENTRY.PLAYER_EDIT_MOBILE, SLIDE_RENDER_ENTRY.PLAYER_EDIT_PC].includes(entry)) {
            // 演示态 直接将编辑区的渲染完成当成可视区缩略图完成，因为基本是没有缩略图的，即使有的场景有，暂时也可以忽略
            const currentSlideKey = (await this.getServicesPromise()).selectionService.currentSlide?.key;
            if (!this.entrySlideRenderCostMap.has(this.getRenderSlideKey(entry, currentSlideKey))) {
                visibleSlideRenderComplete = false;
            }
        }
        if ([SLIDE_RENDER_ENTRY.THUMBNAIL_PC, SLIDE_RENDER_ENTRY.THUMBNAIL_MOBILE].includes(entry)) {
            if ((this.pageSize > 1) && (this.listPanelSize <= 1)) {
                // 列表还未构建(当只有1页时，露出速度快于visibleSlideKeyList初始化，这种情况忽略)
                visibleSlideRenderComplete = false;
            }
            // 普通PC/M场景 判断缩略图是否完成
            this.visibleSlideKeyList.forEach((key) => {
                if (!this.entrySlideRenderCostMap.has(this.getRenderSlideKey(entry, key))) {
                    visibleSlideRenderComplete = false;
                }
            });
        }
        // 记录此缩略图的首次渲染时间
        const index = slide.parent?.children.findIndex(e => e === slide) || 0;
        this.listCellFirstLoadTimeArr[index] = this.listCellFirstLoadTimeArr[index] || renderCompleteTime;
        // 如果首屏内的都已渲染，则通知
        if (visibleSlideRenderComplete) {
            this.notifyCompleted(IStatePointEnum.visibleSlideRender, renderCompleteTime, entry);
        }
    }

    /**
     * 分析首屏是否渲染完成
     * @param renderCompleteTime
     * @param entry
     * @private
     */
    private analyseFirstSlideRenderComplete(renderCompleteTime: number, entry: SLIDE_RENDER_ENTRY) {
        this.notifyCompleted(IStatePointEnum.firstSlideRender, renderCompleteTime, entry);
    }

    /**
     * 分析所有缩略图占位是否渲染完成
     * @param renderCompleteTime
     * @param entry
     * @private
     */
    private analyseAllThumbnailSiteComplete(renderCompleteTime: number, entry: SLIDE_RENDER_ENTRY) {
        if (this.listPanelSize !== this.pageSize) {
            return;
        }
        this.notifyCompleted(IStatePointEnum.allThumbnailSite, renderCompleteTime, entry);
    }

    /**
     * 分析编辑区是否渲染完成
     * @param renderCompleteTime
     * @param entry
     * @param slide
     * @private
     */
    private analyseEditSlideRenderComplete(renderCompleteTime: number, entry: SLIDE_RENDER_ENTRY, slide: ISlideModel) {
        if ([
            SLIDE_RENDER_ENTRY.THUMBNAIL_PC,
            SLIDE_RENDER_ENTRY.THUMBNAIL_MOBILE,
            SLIDE_RENDER_ENTRY.PLAYER_THUMBNAIL_MOBILE,
            SLIDE_RENDER_ENTRY.PLAYER_THUMBNAIL_PC,
        ].includes(entry)) {
            // 缩略图直接return
            return;
        }
        this.notifyCompleted(IStatePointEnum.editSlideRender, renderCompleteTime, entry, slide);
    }

    /**
     * 通知各时机渲染完成
     * @param type
     * @param renderCompleteTime
     * @param entry
     * @param slide
     * @private
     */
    private notifyCompleted(
        type: IStatePointEnum,
        renderCompleteTime: number,
        entry: SLIDE_RENDER_ENTRY,
        slide?: ISlideModel,
    ): void {
        if (type !== IStatePointEnum.editSlideRender) {
            const params = { time: renderCompleteTime, entry };
            this.statePoints[type].emitPointComplete(params);
        } else if (type === IStatePointEnum.editSlideRender && slide) {
            const params = { time: renderCompleteTime, entry, slide };
            this.statePoints[type].emitPointComplete(params);
        }
    }
}
